import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  Heading02,
  HeadingCompact01,
  Short01,
} from 'module/ui/components/styles';
import { faArrowRight, faXmark } from '@fortawesome/pro-regular-svg-icons';
import {
  faLightbulbOn,
  faTriangleExclamation,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { Button, Icon } from '@rocketlane/ui';
import { SignalType } from 'api/signal/types';
import { FC } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: var(--white-100);
  border: 1px solid var(--divider-default);
  border-radius: var(--border-radius-small);
  padding: var(--spacing-07);
  margin: var(--spacing-06);
  box-shadow: var(--shadow-elevation-1);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-05);
`;

const Title = styled.div`
  ${Heading02};
  color: var(--text-primary);
`;

const Description = styled.div`
  ${Short01};
  color: var(--text-secondary);
  margin-top: var(--spacing-03);
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--spacing-06);
  margin-top: var(--spacing-07);

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--scarlet-gray-10);
  border: 1px solid var(--divider-subtle);
  border-radius: var(--border-radius-small);
  padding: var(--spacing-06);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-04);
`;

const CardIcon = styled.div<{ $color: string }>`
  color: ${(p) => p.$color};
  font-size: 18px;
`;

const CardTitle = styled.div`
  ${HeadingCompact01};
  color: var(--text-primary);
`;

const CardBody = styled.div`
  ${Short01};
  color: var(--text-secondary);
  flex: 1;
`;

const SetupButton = styled(Button)`
  width: 100%;

  && {
    background: var(--gray-10);
    border-color: var(--gray-10);
    color: var(--text-primary);
  }
`;

export interface RecommendedSignalsEmptyStateProps {
  onDismiss?: VoidFunction;
  onSetup: (signalType: SignalType) => void;
}

export const RecommendedSignalsEmptyState: FC<
  RecommendedSignalsEmptyStateProps
> = ({ onDismiss, onSetup }) => {
  const recommended = [
    {
      key: 'churn-threat',
      title: 'Churn threat',
      description:
        'Triggers when customers mention cancellation, pricing concerns, or dissatisfaction.',
      signalType: SignalType.Risk,
      icon: faTriangleExclamation,
      color: 'var(--red-60)',
    },
    {
      key: 'testimonials',
      title: 'Testimonials',
      description:
        'Triggers when customers express strong positive sentiment, praise outcomes, or recommend your product unprompted.',
      signalType: SignalType.Opportunity,
      icon: faLightbulbOn,
      color: 'var(--green-60)',
    },
    {
      key: 'feature-request',
      title: 'Feature request',
      description:
        'Triggers when customers request new features or suggest improvements to existing ones.',
      signalType: SignalType.Operational,
      icon: faWrench,
      color: 'var(--gray-60)',
    },
  ] as const;

  return (
    <Container>
      <HeaderRow>
        <div>
          <Title>Recommended Signals</Title>
          <Description>
            Some description goes here about this feature which can help the
            customers.
          </Description>
        </div>
        {onDismiss ? (
          <Button
            variant="ghost"
            onClick={onDismiss}
            aria-label="Close"
            leftIcon={<Icon icon={faXmark} />}
          />
        ) : null}
      </HeaderRow>

      <Cards>
        {recommended.map((item) => (
          <Card key={item.key}>
            <CardIcon $color={item.color}>
              <FontAwesomeIcon icon={item.icon} />
            </CardIcon>
            <CardTitle>{item.title}</CardTitle>
            <CardBody>{item.description}</CardBody>
            <SetupButton
              onClick={() => onSetup(item.signalType)}
              rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
            >
              Set it up
            </SetupButton>
          </Card>
        ))}
      </Cards>
    </Container>
  );
};
